import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSupabase } from './SupabaseContext';

// Who is an admin, and which modules are switched on.
// Both are enforced in the database (RLS + admin_* functions); this context only decides
// what the app shows. If the admin SQL hasn't been run yet, nobody is an admin and every
// module stays on, so the app works exactly as before.
const AccessContext = createContext({
  isAdmin: false,
  adminChecked: false,
  modules: [],
  isEnabled: () => true,
  refreshModules: () => {},
});

export const AccessProvider = ({ children }) => {
  const { supabase, user } = useSupabase();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const [modules, setModules] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setAdminChecked(false);
    if (!user) {
      setIsAdmin(false);
      setAdminChecked(true);
      return undefined;
    }
    supabase.rpc('is_admin').then(({ data, error }) => {
      if (cancelled) return;
      if (error) console.warn('Admin check unavailable:', error.message);
      setIsAdmin(!error && data === true);
      setAdminChecked(true);
    });
    return () => { cancelled = true; };
  }, [supabase, user]);

  const refreshModules = useCallback(async () => {
    const { data, error } = await supabase
      .from('app_modules')
      .select('key, name, description, enabled, sort_order, updated_at')
      .order('sort_order');
    if (error) {
      console.warn('Module settings unavailable:', error.message);
      setModules([]);
      return;
    }
    setModules(data || []);
  }, [supabase]);

  // Reload when the user changes (signed in / out)
  useEffect(() => { refreshModules(); }, [refreshModules, user]);

  const value = useMemo(() => {
    const off = new Set(modules.filter(m => m.enabled === false).map(m => m.key));
    return {
      isAdmin,
      adminChecked,
      modules,
      // Unknown modules (or no settings table) count as on
      isEnabled: (key) => !key || !off.has(key),
      refreshModules,
    };
  }, [isAdmin, adminChecked, modules, refreshModules]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

export const useAccess = () => useContext(AccessContext);
